import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Backdrop,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@material-ui/core';
import logoCidadaniaJa from 'global/sources/img/logo-cidadania-ja.png';
import Root from 'global/modules/Root';
import { newClientFields } from 'constants/newClient';
import { fetch as apiFetch, create as apiCreate } from 'api';
import {
  formatUser,
  storeUserSession,
  storeSessionToken,
  storeSessionTokenValidTime,
} from 'helpers';
import ClientForm from './NewClientForm/Form';
import { createPostObject } from './NewClientForm/functions';
import { useStyles } from './style';

// Página do link único da venda (22/10/2026): o vendedor criou a venda pelo
// número do negócio e mandou este link. O cliente preenche os mesmos dados
// do /novo-cliente; a api gera o contrato e devolve o login, e ele cai
// direto na página do contrato para assinar.

const mensagemDoErro = (error) => {
  const data = error && error.response && error.response.data;
  if (data && data.errors) {
    return Object.values(data.errors).map((m) => (Array.isArray(m) ? m[0] : m)).join(' ');
  }
  return (data && data.message) || 'Não foi possível enviar agora. Tente de novo em alguns minutos.';
};

function Aviso({ titulo, texto, classes, entrar = false }) {
  return (
    <Paper elevation={3}>
      <Typography variant="h5" paragraph>{titulo}</Typography>
      <Typography paragraph>{texto}</Typography>
      {entrar && (
        <Link to="/login" className={classes.link}>
          <Button variant="contained" color="primary">Entrar na Central</Button>
        </Link>
      )}
    </Paper>
  );
}

function NewClientFromSale({ match }) {
  const classes = useStyles();
  const { token } = match.params;
  const [status, setStatus] = useState('loading');
  const [form, setForm] = useState(newClientFields);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch(`salesscope/by-token/${token}`)
      .then((res) => {
        if (res.status === 'filled') {
          setStatus('filled');
          return;
        }
        if (!res.ready) {
          setStatus('not_ready');
          return;
        }
        setForm({
          ...newClientFields,
          name: res.prefill.name || '',
          email: res.prefill.email || '',
          phone: res.prefill.phone || '',
        });
        setStatus('form');
      })
      .catch(() => setStatus('invalid'));
  }, [token]);

  const enviar = (postObject) => {
    setSending(true);
    setError('');
    apiCreate(`salesscope/by-token/${token}/customer`, { client: postObject.client })
      .then((res) => {
        if (res.auto_login && res.login) {
          localStorage.clear();
          storeUserSession(formatUser(res.login.user));
          storeSessionToken(res.login.access_token);
          storeSessionTokenValidTime(res.login.expires_in);
          window.location.href = `/contrato/${res.scope_id}`;
          return;
        }
        setSending(false);
        setStatus('sent_without_login');
      })
      .catch((err) => {
        setSending(false);
        if (err && err.response && err.response.status === 409) {
          setStatus('filled');
          return;
        }
        setError(mensagemDoErro(err));
      });
  };

  return (
    <Root setTitle="Seus dados" paddingTop>
      <Container maxWidth="md" style={{ paddingBottom: 1 }}>
        <img src={logoCidadaniaJa} alt="Cidadania Já" className={classes.logoImg} />
        {status === 'loading' && (
          <div style={{ textAlign: 'center' }}><CircularProgress /></div>
        )}
        {status === 'invalid' && (
          <Aviso
            classes={classes}
            titulo="Link inválido"
            texto="Confira o link que você recebeu ou fale com o seu vendedor."
          />
        )}
        {status === 'not_ready' && (
          <Aviso
            classes={classes}
            titulo="Sua proposta ainda está sendo preparada"
            texto="O seu vendedor ainda está montando a proposta. Tente de novo mais tarde ou fale com ele."
          />
        )}
        {status === 'filled' && (
          <Aviso
            classes={classes}
            entrar
            titulo="Seus dados já foram recebidos"
            texto="O contrato e a senha de acesso foram enviados para o seu e-mail. Entre na Central para ler e assinar."
          />
        )}
        {status === 'sent_without_login' && (
          <Aviso
            classes={classes}
            entrar
            titulo="Recebemos os seus dados!"
            texto="Você já tem cadastro com a gente. Enviamos o contrato e a senha de acesso para o e-mail da sua conta. Entre na Central para ler e assinar."
          />
        )}
        {status === 'form' && (
          <>
            <ClientForm
              form={form}
              setForm={setForm}
              externalUser
              classes={classes}
              inputClientId={false}
              createCustomer={(value) => enviar(value || createPostObject(form))}
              action={null}
              sentValues
              errorMessage={null}
            />
            {error && (
              <Typography color="error" variant="h6" style={{ paddingTop: 16 }}>{error}</Typography>
            )}
          </>
        )}
      </Container>
      <Backdrop open={sending} style={{ zIndex: 2000, color: '#fff', flexDirection: 'column' }}>
        <CircularProgress color="inherit" />
        <Typography style={{ marginTop: 16 }}>Gerando o seu contrato…</Typography>
      </Backdrop>
    </Root>
  );
}

export default NewClientFromSale;
